/**
 * The nearby-deals block (feedback ticket 65: "if we search in Mayak
 * Residence, below it the flats in that building and, in a separate
 * catalogue, the favourable flats near there"): which searches get it,
 * what it asks GET /api/listings/nearby-deals for, where its "see all"
 * link goes and how a card says its distance. Pure, so the listings page,
 * the residence page and the unit tests read the same rules.
 */
import { widenRooms } from "@/lib/listing-search-params";

/** Land, shops and garages carry no verdict, so no deals either. */
const NO_VERDICT = new Set(["land", "commercial", "garage"]);

/** A place picked next to the residence makes the search more than one
 *  residence (the places add up), and the block is about the residence. */
const OTHER_PLACES = [
  "location_ids", "city", "street", "draw", "center_lat", "districts", "district", "metro", "sub_location",
];

export interface NearbyQuery {
  /** The residence (its cluster id, the URL's complex_slug). */
  clusterId: string;
  /** The search's side: rent (true), sale (false) or both (null). */
  leased: boolean | null;
  /** The URL's room values ("5" is five and more). */
  rooms: string[];
  /** A home search's category (apartment or house), else null. */
  category: string | null;
}

interface ParamsLike {
  get(key: string): string | null;
  getAll(key: string): string[];
}

/** The block's query for a listings search, or null when the search is
 *  not exactly one residence and nothing else as its place, or asks for
 *  land, shops or garages. */
export function nearbyQueryFromSearch(sp: ParamsLike): NearbyQuery | null {
  const slugs = sp.getAll("complex_slug").filter(Boolean);
  if (slugs.length !== 1) return null;
  if (OTHER_PLACES.some((k) => sp.getAll(k).some(Boolean))) return null;
  const category = sp.get("category");
  if (category && NO_VERDICT.has(category)) return null;
  if (sp.getAll("listing_kind").some((k) => NO_VERDICT.has(k))) return null;
  const leased = sp.get("leased");
  return {
    clusterId: slugs[0],
    // Absent is sale, the listings page's default; "all" is both sides.
    leased: leased === "true" ? true : leased === "all" ? null : false,
    rooms: sp.getAll("rooms").filter((r) => /^\d{1,2}$/.test(r)),
    category: category === "apartment" || category === "house" ? category : null,
  };
}

/** What the block asks the API for. */
export function nearbyApiParams(q: NearbyQuery): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = { complex_slug: q.clusterId };
  if (q.leased !== null) params.leased = String(q.leased);
  const rooms = widenRooms(q.rooms);
  if (rooms.length) params.rooms = rooms;
  if (q.category) params.category = q.category;
  return params;
}

/** All of them on the listings page: the circle the block drew around the
 *  residence, the same side, rooms and category, the three deal tiers
 *  ("great" carries the exceptional ones), the strongest deals first. */
export function nearbyAllHref(
  q: NearbyQuery, center: { lat: number; lng: number }, radiusKm: number,
): string {
  const p = new URLSearchParams();
  p.set("center_lat", center.lat.toFixed(5));
  p.set("center_lng", center.lng.toFixed(5));
  p.set("radius_km", String(radiusKm));
  if (q.leased === true) p.set("leased", "true");
  else if (q.leased === null) p.set("leased", "all");
  for (const r of q.rooms) p.append("rooms", r);
  if (q.category) p.set("category", q.category);
  p.append("deal_tier", "great");
  p.append("deal_tier", "good");
  p.set("sort", "best_deal");
  return `/listings?${p.toString()}`;
}

/** One decimal in the locale's way ("1,4" in az and ru, "1.4" in en). */
export function decimal(n: number, locale: string): string {
  const s = n.toFixed(1);
  return locale === "en" ? s : s.replace(".", ",");
}

/** A distance as a number and its unit: metres under a kilometre (to the
 *  10 m, at least 10), else kilometres to one decimal. */
export function distanceParts(km: number, locale: string): { unit: "m" | "km"; n: string } {
  const m = Math.round(km * 100) * 10;
  if (m < 1000) return { unit: "m", n: String(Math.max(10, m)) };
  return { unit: "km", n: decimal(km, locale) };
}
